"use client";
import React, { useState, useEffect, useRef } from 'react';
import { X, MessageSquare, Users, Calendar, Clock, Send, DollarSign } from 'lucide-react';

const BulkSMSModal = ({ isOpen, onClose, campaign, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    message: '',
    contactLists: [],
    template: 'promotional',
    scheduledAt: '',
 
    sendImmediately: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [messageStats, setMessageStats] = useState({
    characters: 0,
    smsCount: 1,
    estimatedCost: 0
  });
  const modalRef = useRef(null);

const contactLists = [
  { id: 1, name: 'VIP Customers' },
  { id: 2, name: 'Newsletter Subscribers' },
  { id: 3, name: 'Recent Orders' },
  { id: 4, name: 'All Customers' },
  { id: 5, name: 'Delivered Orders' }
];
  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign.name || '',
        message: campaign.message || '',
        contactLists: campaign.contactLists || [],
        template: campaign.template || 'promotional',
        scheduledAt: campaign.scheduledAt ? campaign.scheduledAt.slice(0, 16) : '',
    
        sendImmediately: false
      });
    } else {
      setFormData({
        name: '',
        message: '',
        contactLists: [],
        template: 'promotional',
        scheduledAt: '',
       
        sendImmediately: false
      });
    }
    setErrors({});
  }, [campaign, isOpen]);

useEffect(() => {
  const characters = formData.message.length;
  const smsCount = Math.ceil(characters / 160) || 1;

  const totalRecipients = formData.contactLists.length; // just number of selected lists

  const estimatedCost = smsCount * totalRecipients * 0.10;

  setMessageStats({
    characters,
    smsCount,
    estimatedCost
  });
}, [formData.message, formData.contactLists]);

  // Handle escape key and focus management
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleContactListChange = (listName, isChecked) => {
    setFormData(prev => ({
      ...prev,
      contactLists: isChecked
        ? [...prev.contactLists, listName]
        : prev.contactLists.filter(name => name !== listName)
    }));

    if (errors.contactLists) {
      setErrors(prev => ({
        ...prev,
        contactLists: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Campaign name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Campaign name must be at least 3 characters';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message content is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (formData.message.length > 1600) {
      newErrors.message = 'Message is too long (max 1600 characters)';
    }

    if (formData.contactLists.length === 0) {
      newErrors.contactLists = 'Please select at least one contact list';
    }

    if (!formData.sendImmediately && !formData.scheduledAt) {
      newErrors.scheduledAt = 'Please select a schedule time or choose to send immediately';
    }

    if (formData.scheduledAt && new Date(formData.scheduledAt) <= new Date()) {
      newErrors.scheduledAt = 'Scheduled time must be in the future';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // 🔥 ADD THIS
    await onSave(formData);
  };

  
  const insertTemplate = (template) => {
    const templates = {
      greeting: 'Hi {name}, ',
      orderUpdate: 'Your order #{orderNumber} has been {status}. ',
      promotion: 'Special offer! Get {discount}% off with code {code}. ',
      reminder: 'Reminder: {event} is scheduled for {date}. ',
      thankYou: 'Thank you for your purchase! We appreciate your business. '
    };

    setFormData(prev => ({
      ...prev,
      message: prev.message + templates[template]
    }));
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full max-w-3xl bg-gradient-to-br from-orange-50 to-orange-200 bg-opacity-95 shadow-2xl z-50 transform transition-all duration-300 ease-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          borderRadius: '10px 0 0 10px'
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="fixed left-4 top-4 w-8 h-8 bg-black text-white rounded-full hover:bg-gray-800 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white z-50 flex items-center justify-center shadow-lg"
          aria-label="Close SMS campaign modal"
          disabled={isLoading}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Modal Header */}
        <div className="sticky top-0 bg-orange-50 p-8 z-10 border-b border-orange-200">
          <div className="text-center">
            <h2 id="modal-title" className="text-3xl font-bold text-teal-800 mb-2">
              {campaign ? 'Edit SMS Campaign' : 'Create SMS Campaign'}
            </h2>
            <div className="w-16 h-1 bg-orange-300 rounded-full mx-auto"></div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-8 space-y-8 min-h-[700px] rounded-[25px] m-4 bg-gray-400 bg-opacity-20"
        >
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              {errors.general}
            </div>
          )}

          {/* Campaign Name */}
          <div className="space-y-3">
            <label htmlFor="name" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Campaign Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter campaign name..."
              className={`w-full px-4 py-3 bg-white bg-opacity-80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 ${
                errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-orange-200'
              }`}
              disabled={isLoading}
              maxLength={100}
            />
            <div className="flex justify-between items-center">
              {errors.name ? (
                <p className="text-red-500 text-sm">{errors.name}</p>
              ) : (
                <p className="text-xs text-gray-600">Give your campaign a descriptive name</p>
              )}
              <span className="text-xs text-gray-600 font-medium">{formData.name.length}/100</span>
            </div>
          </div>

          {/* Template Type */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Campaign Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="campaign_type"
                  value="promotional"
                  checked={formData.template === 'promotional'}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                  formData.template === 'promotional'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300 bg-white group-hover:border-blue-400'
                }`}>
                  {formData.template === 'promotional' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className={`ml-3 text-sm font-medium transition-colors ${
                  formData.template === 'promotional' ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Promotional
                </span>
              </label>

              <label className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="campaign_type"
                  value="transactional"
                  checked={formData.template === 'transactional'}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                  formData.template === 'transactional'
                    ? 'border-green-500 bg-green-500'
                    : 'border-gray-300 bg-white group-hover:border-green-400'
                }`}>
                  {formData.template === 'transactional' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className={`ml-3 text-sm font-medium transition-colors ${
                  formData.template === 'transactional' ? 'text-green-700' : 'text-gray-700'
                }`}>
                  Transactional
                </span>
              </label>
            </div>
          </div>

          {/* Message Content */}
          <div className="space-y-3">
            <label htmlFor="message" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Message Content *
            </label>

            {/* Quick Templates */}
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                type="button"
                onClick={() => insertTemplate('greeting')}
                className="px-3 py-1 text-xs bg-teal-100 text-teal-700 rounded-full hover:bg-teal-200 transition-colors"
                disabled={isLoading}
              >
                + Greeting
              </button>
              <button
                type="button"
                onClick={() => insertTemplate('orderUpdate')}
                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                disabled={isLoading}
              >
                + Order Update
              </button>
              <button
                type="button"
                onClick={() => insertTemplate('promotion')}
                className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
                disabled={isLoading}
              >
                + Promotion
              </button>
              <button
                type="button"
                onClick={() => insertTemplate('reminder')}
                className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200 transition-colors"
                disabled={isLoading}
              >
                + Reminder
              </button>
              <button
                type="button"
                onClick={() => insertTemplate('thankYou')}
                className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                disabled={isLoading}
              >
                + Thank You
              </button>
            </div>

            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Enter your SMS message..."
              rows={6}
              className={`w-full px-4 py-3 bg-white bg-opacity-80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 resize-none ${
                errors.message ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-orange-200'
              }`}
              disabled={isLoading}
              maxLength={1600}
            />

            {/* Message Statistics */}
            <div className="bg-white bg-opacity-60 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Characters:</span>
                <span className={`font-medium ${messageStats.characters > 160 ? 'text-orange-600' : 'text-gray-900'}`}>
                  {messageStats.characters}/1600
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">SMS Count:</span>
                <span className="font-medium text-gray-900">{messageStats.smsCount}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Estimated Cost:</span>
                <span className="font-medium text-gray-900">₹{messageStats.estimatedCost.toFixed(2)}</span>
              </div>
            </div>

            {errors.message ? (
              <p className="text-red-500 text-sm">{errors.message}</p>
            ) : (
              <p className="text-xs text-gray-600">Use {'{name}'}, {'{orderNumber}'}, {'{code}'} for personalization</p>
            )}
          </div>

          {/* Contact Lists */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Select Contact Lists *
            </label>
            <div className="space-y-3 max-h-48 overflow-y-auto bg-white bg-opacity-60 rounded-lg p-4">
              {contactLists.map((list) => (
              <label key={list.id} className="flex items-center cursor-pointer group">
  <div className="flex items-center">
    <input
      type="checkbox"
      checked={formData.contactLists.includes(list.name)}
      onChange={(e) => handleContactListChange(list.name, e.target.checked)}
      className="sr-only"
      disabled={isLoading}
    />
    <div className={`relative w-5 h-5 rounded border-2 transition-all duration-300 ${
      formData.contactLists.includes(list.name)
        ? 'border-teal-500 bg-teal-500'
        : 'border-gray-300 bg-white group-hover:border-teal-400'
    }`}>
      {formData.contactLists.includes(list.name) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </div>
    <span className="ml-3 text-sm font-medium text-gray-900">
      {list.name}
    </span>
  </div>
</label>
              ))}
            </div>
            {errors.contactLists && (
              <p className="text-red-500 text-sm">{errors.contactLists}</p>
            )}
          </div>

          {/* Scheduling */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Scheduling
            </label>

            <div className="space-y-4">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  name="sendImmediately"
                  checked={formData.sendImmediately}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div className={`relative w-5 h-5 rounded border-2 transition-all duration-300 ${
                  formData.sendImmediately
                    ? 'border-green-500 bg-green-500'
                    : 'border-gray-300 bg-white group-hover:border-green-400'
                }`}>
                  {formData.sendImmediately && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                <span className="ml-3 text-sm font-medium text-gray-900">Send immediately</span>
              </label>

              {!formData.sendImmediately && (
                <div>
                  <label htmlFor="scheduledAt" className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule for later
                  </label>
                  <input
                    type="datetime-local"
                    id="scheduledAt"
                    name="scheduledAt"
                    value={formData.scheduledAt}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-white bg-opacity-80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 ${
                      errors.scheduledAt ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-orange-200'
                    }`}
                    disabled={isLoading}
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>
              )}
            </div>

            {errors.scheduledAt && (
              <p className="text-red-500 text-sm">{errors.scheduledAt}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-8 pb-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-8 py-4 bg-white bg-opacity-80 text-gray-700 rounded-xl hover:bg-opacity-100 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-400 font-semibold border border-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-8 py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-teal-400 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  {formData.sendImmediately ? <Send className="w-5 h-5" /> : <Calendar className="w-5 h-5" />}
                  {formData.sendImmediately ? 'Send Now' : (campaign ? 'Update Campaign' : 'Schedule Campaign')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default BulkSMSModal;
